import { useContext, useEffect, useState } from "react";

import ReactGA from "react-ga4";

import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useParams } from "react-router-dom";

import ActionButton from "../components/formInputs/buttons/ActionButton";
import Archives from "../components/guardian/Archives";
import GuardianEprDao from "../dao/GuardianEprDao";
import LoadingSvg3 from "../components/loadingSvg/LoadingSvg3";
import MostRecentEprs from "../components/guardian/MostRecentEprs";
import RbA from "../components/rba/RbA";
import StudentInfoDao from "../dao/StudentInfoDao";
import StudentSelect from "../components/guardian/StudentSelect";
import T from "../components/translations/Translations";

import { GA_MID } from "../const/GaConst";
import { getEpoch, getTodayWithTime } from "../utils/DateFormatter";
import { GlobalContext } from "../components/contextProvider/ContextProvider";

import "../styles/GuardianHome.scss";

/**
 * @description This is the home page for guardians
 * @name GuardianHome
 * @constructor
 * @returns
 */
const GuardianHome = () => {
    const { studentNumber } = useParams();

    const location = useLocation();
    const currentPath = location.pathname;
    const navigate = useNavigate();

    const { state, dispatch } = useContext(GlobalContext);
    const { allLocationDtos, eprActionWindows, schoolYearDto, studentsIc, token, username } = state || {};

    const [activeTab, setActiveTab] = useState("archive");
    const [eprs, setEprs] = useState(null);
    const [loader, setLoader] = useState(false);
    const [nextStartDate, setNextStartDate] = useState(null);
    const [resultMessages, setResultMessages] = useState([]);
    const [routeTab, setRouteTab] = useState(0);
    const [selectedStudent, setSelectedStudent] = useState(null);

    const allowedRolesArray = ["GUARDIAN"];

    const archivedFail1 = T({ key: "Archived_Fail_1" });
    const archivedFail2 = T({ key: "Archived_Fail_2" });
    const archivedSuccess = T({ key: "Archived_Success" });
    const mostRecentTitle = T({ key: "Most_Recent_Title" });
    const eprViewArchive = T({ key: "EPR_View_Archive" });

    /**
     * Try to prevent auto event updates being sent
     * This may require additional settings adjustments in the analytics dashboard settings, as follows:
     * Navigate to the Admin section of your Google Analytics account.
     * In the Property column, click Data Streams and select your web data stream.
     * Under Enhanced measurement, click the gear icon (settings).
     * Toggle off the Page views event under the "Events" section, or turn off "Page changes based on browser history events" if available, then save your changes.
     * else the following options may help:
     * - set the "send_page_view" setting to false
     * - set the "enhanced_measurement_protocol" setting to true
     * - set the "allow_ad_personalization_signals" setting to false
     * - set the "non_interaction" setting to true
     * - set the "excluded_domains" setting to the domains that are not allowed to send events
     * @name initializeReactGA
     */
    ReactGA.initialize(GA_MID, {
        gtagOptions: {
            send_page_view: false
        }
    });

    // helper to convert blob to text
    const blobToText = async (blob) => {
        return await new Response(blob).text();
    };

    const registerDownload = (type) => {
        const environ = process.env.NODE_ENV !== "production" ? " -> dev" : "";
        ReactGA.event({
            category: "Documents",
            action: `EPR - Download PDF${environ}`,
            label: type // identify which PDF was downloaded: "Archive", "Current"
        });
    };

    const downloadPdf = async (key, schoolYearName, fromArchive = true) => {
        let type = fromArchive ? "Archived Report Cards" : "Most Recent Report Card";
        try {
            const options = {
                action: "eprPdfDownload",
                gradingProfileId: key,
                studentNumber: selectedStudent.studentNumber,
                token
            };
            setLoader(true);
            const response = await GuardianEprDao(options);
            if (response) {
                // This will open the PDF in a new tab AND download the file simultaneously
                const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
                window.open(url, "_blank");
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute(
                    "download",
                    `epr_${selectedStudent?.lastName}_${selectedStudent?.firstName}_${selectedStudent.studentNumber}.pdf`
                );
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
                if (fromArchive) {
                    setResultMessages((prevState) => {
                        return [
                            {
                                error: false,
                                message: `${archivedSuccess} ${schoolYearName}.`
                            },
                            ...(prevState || [])
                        ];
                    });
                } else {
                    toast.success(`${archivedSuccess} ${schoolYearName}.`, {
                        autoClose: 3000
                    });
                }
                type = fromArchive ? "Archive - Success" : "Current - Success";
            }
        } catch (error) {
            if (error.response && error.response.data instanceof Blob) {
                const text = await blobToText(error.response.data);
                if (fromArchive) {
                    setResultMessages((prevState) => {
                        return [
                            {
                                error: true,
                                message: (
                                    <>
                                        {archivedFail1} {schoolYearName}. {archivedFail2} {text}
                                    </>
                                )
                            },
                            ...(prevState || [])
                        ];
                    });
                } else {
                    toast.error(
                        <>
                            {archivedFail1} {schoolYearName}. {archivedFail2} {text}
                        </>,
                        {
                            autoClose: 3000
                        }
                    );
                }
                type = fromArchive ? "Archive - Fail" : "Current - Fail";
            }
        } finally {
            setLoader(false);
            registerDownload(type);
        }
    };

    /**
     * Get guardian's IC students info
     */
    useEffect(() => {
        if (!token || !username) {
            setLoader(false);
            return;
        }

        /**
         * @name fetchStudents
         * @description Get a list of all IC students for the school year and sort them oldest first
         * @async
         */
        const fetchStudents = async () => {
            try {
                setLoader(true);
                const options = {
                    action: "studentsByGuardianRead",
                    params: {
                        checkForActiveEnrollment: true
                    },
                    token,
                    username
                };
                const response = await StudentInfoDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    payload.sort((a, b) => {
                        if (getEpoch(a.birthdate) === getEpoch(b.birthdate)) {
                            if (a.lastName === b.lastName) {
                                return a.firstName.localeCompare(b.firstName);
                            }

                            return a.lastName.localeCompare(b.lastName);
                        }

                        return getEpoch(a.birthdate) > getEpoch(b.birthdate) ? 1 : -1;
                    });

                    dispatch({
                        type: "StudentsIc",
                        studentsIc: payload
                    });
                }
            } catch (error) {
                console.error(`Unable to retrieve Guardian's students:\n`, error);
            } finally {
                setLoader(false);
            }
        };
        if (!studentsIc) {
            fetchStudents();
        }
    }, [dispatch, studentNumber, studentsIc, token, username]);

    useEffect(() => {
        if (!studentNumber || !studentsIc) {
            return;
        }
        const student = studentsIc.find(
            (student) => parseInt(student.studentNumber, 10) === parseInt(studentNumber, 10)
        );
        if (student) {
            setSelectedStudent(student);
        }
    }, [studentNumber, studentsIc]);

    useEffect(() => {
        if (!schoolYearDto || !selectedStudent || !token) {
            return;
        }

        let isMounted = true;

        const fetchData = async () => {
            setLoader(true);

            try {
                // Check EPR Status
                let statusPayload = false;
                try {
                    const statusOptions = {
                        action: "eprStatusRead",
                        schoolYearKey: schoolYearDto.key,
                        studentNumber: selectedStudent.studentNumber,
                        token
                    };
                    const statusResponse = await GuardianEprDao(statusOptions);

                    if (statusResponse?.data?.payload) {
                        statusPayload = statusResponse.data.payload;
                    }
                } catch (error) {
                    console.error("Unable to retrieve EPR status:\n", error);
                    statusPayload = false;
                }

                // Fetch EPRs (Only if statusPayload is true)
                if (isMounted && statusPayload) {
                    try {
                        const eprsOptions = {
                            action: "eprGradingProfileRead",
                            schoolYearKey: schoolYearDto.key,
                            studentNumber: selectedStudent.studentNumber,
                            token
                        };
                        const eprsResponse = await GuardianEprDao(eprsOptions);

                        if (isMounted && eprsResponse?.data) {
                            const { payload } = eprsResponse.data;
                            if (payload && payload.length > 0) {
                                // sometimes payload comes back as [null] instead of []
                                if (payload[0]) {
                                    setEprs(payload);
                                }
                            }
                        }
                    } catch (error) {
                        console.error("Unable to retrieve most recent EPR:\n", error);
                        if (isMounted) {
                            setEprs(null);
                        }
                    }
                }
            } finally {
                if (isMounted) {
                    setLoader(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [schoolYearDto, selectedStudent, token]);

    useEffect(() => {
        if (eprActionWindows || !allLocationDtos || !schoolYearDto || !selectedStudent || !token) {
            return;
        }

        let locKey = "default";
        const locMatch = allLocationDtos.filter((location) => {
            const { icId } = location;
            // .some() stops iterating as soon as it finds a match, unlike .filter()
            return icId.some((item) => parseInt(item, 10) === selectedStudent.schoolId);
        });

        if (locMatch.length > 0) {
            locKey = locMatch[0].key;
        }

        const fetchActionWindows = async () => {
            try {
                setLoader(true);
                const options = {
                    action: "eprActionWindowsRead",
                    locationKey: locKey,
                    schoolYearKey: schoolYearDto.key,
                    token
                };
                const response = await GuardianEprDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    dispatch({
                        type: "EprActionWindows",
                        eprActionWindows: payload
                    });
                }
            } catch (error) {
                console.error("Unable to retrieve EPR Action Windows:\n", error);
            } finally {
                setLoader(false);
            }
        };

        fetchActionWindows();
    }, [allLocationDtos, dispatch, eprActionWindows, schoolYearDto, selectedStudent, token]);

    useEffect(() => {
        if (!eprActionWindows || !selectedStudent) {
            return;
        }

        const checkParentViewStatus = () => {
            try {
                const ACTION_NAME = "EPR_PARENT_VIEW";
                // Filter to relevant actions
                const viewActions = eprActionWindows.filter((action) => action.action === ACTION_NAME);
                // Group windows by 'differentiator' (e.g., '21', '22') to handle overrides
                const windowsByDifferentiator = {};

                viewActions.forEach((action) => {
                    const diffKey = action.differentiator;
                    const currentStored = windowsByDifferentiator[diffKey];
                    // If we already have a LOCATION match for this differentiator,
                    // we keep it and ignore everything else.
                    if (currentStored && currentStored.whoType === "LOCATION") {
                        return;
                    }
                    // If the new action is LOCATION, it automatically overrides any
                    // existing DEFAULT (or empty slot).
                    if (action.whoType === "LOCATION") {
                        windowsByDifferentiator[diffKey] = action;
                    }
                    // If the new action is DEFAULT, we only store it if the slot is empty.
                    else if (action.whoType === "DEFAULT" && !currentStored) {
                        windowsByDifferentiator[diffKey] = action;
                    }
                });

                // Check if we are within ANY of the resolved valid windows
                const validWindows = Object.values(windowsByDifferentiator);
                const now = getEpoch(getTodayWithTime());

                let isActive = false;
                let nextStart = null;

                validWindows.forEach((action) => {
                    const start = getEpoch(action.startDate);
                    const end = getEpoch(action.endDate);

                    // Check if currently active
                    if (start <= now && now < end) {
                        isActive = true;
                    }

                    // Check for future windows to determine the "next" available date
                    if (start > now) {
                        // If we haven't found a nextStart yet, OR this one is sooner than the current winner
                        if (!nextStart || start < getEpoch(nextStart)) {
                            nextStart = action.startDate;
                        }
                    }
                });
                // If active, we don't need a next start date. If inactive, show the next available one.
                setNextStartDate(isActive ? null : nextStart);
                if (routeTab === 0) {
                    setActiveTab(isActive ? "epr" : "archive");
                }
            } catch (error) {
                console.error("Unable to retrieve Parent View status:\n", error);
            }
        };

        checkParentViewStatus();
    }, [eprActionWindows, routeTab, selectedStudent]);

    useEffect(() => {
        if (currentPath.includes("/guardian/recent/")) {
            setRouteTab(1);
            setActiveTab("epr");
        } else if (currentPath.includes("/guardian/archive/")) {
            setRouteTab(2);
            setActiveTab("archive");
        } else {
            setRouteTab(0);
            navigate("/guardian/student-select");
        }
    }, [currentPath, navigate]);

    return (
        <RbA allowedRoles={allowedRolesArray} redirect="/">
            <ToastContainer style={{ width: "50%" }} />
            <main className="guardian-gutter">
                {selectedStudent ? (
                    <>
                        <div className="guardian-page-tab-group">
                            <ActionButton
                                className={activeTab === "epr" ? "tab tab-first tab-active" : "tab tab-first"}
                                name="epr"
                                label={mostRecentTitle}
                                onClick={() => {
                                    navigate(`/guardian/recent/${studentNumber}`);
                                }}
                            />
                            <ActionButton
                                className={activeTab === "archive" ? "tab tab-active" : "tab"}
                                name="archive"
                                label={eprViewArchive}
                                onClick={() => {
                                    navigate(`/guardian/archive/${studentNumber}`);
                                }}
                            />
                        </div>
                        {activeTab === "epr" && selectedStudent && (
                            <MostRecentEprs
                                downloadPdf={downloadPdf}
                                nextStartDate={nextStartDate}
                                eprs={eprs}
                                loader={loader}
                                setLoader={setLoader}
                                studentInfo={selectedStudent}
                            />
                        )}
                        {activeTab === "archive" && (
                            <div className="guardian-view-container">
                                <Archives
                                    downloadPdf={downloadPdf}
                                    loader={loader}
                                    resultMessages={resultMessages}
                                    selectedStudent={selectedStudent}
                                    setLoader={setLoader}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <StudentSelect studentsIc={studentsIc} />
                )}
            </main>
            {loader && <LoadingSvg3 />}
        </RbA>
    );
};

export default GuardianHome;
